import API from '@/axios/API';
import PropTypes from 'prop-types';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { DataContext } from '../DataProviders/DataProvider';
import { ManualOrderContext } from '../ManualOrderContext/ManualOrderProvider';
import { subscriptionAPI } from '@/api';

const BulkOrderContext = createContext();

const BulkOrderProvider = ({ children }) => {
    const { loginCustomerId, userId, userRole, customerId, setOrderNumber, setOrderId, orderNumber, orderId, token } = useContext(DataContext);
    const { setActiveStep, setData, setLoading, isBulkOrder, setUserPaymentGateway } = useContext(ManualOrderContext);
    const [totalActiveCount, setTotalActiveCount] = useState(0);

    // Ref to prevent duplicate API calls
    const isFetchingRef = useRef(false);

    const customerIdAsPerRole =
        userRole === "Admin" || userRole === "ADMIN" ? 0 : loginCustomerId;

    const hasValidToken = token && token !== "null" && token !== "undefined";

    useEffect(() => {
        if (!hasValidToken) return;
        if (customerIdAsPerRole === undefined || customerIdAsPerRole === null || customerIdAsPerRole === "") return;

        // Prevent duplicate calls
        if (isFetchingRef.current) return;

        const fetchData = async () => {
            try {
                isFetchingRef.current = true;
                const response = await API.get(
                    `${import.meta.env.VITE_BACKEND_API}/dashboard/first-order-count?customerId=${parseInt(customerIdAsPerRole, 10)}`
                );

                // The API returns an array like: [{ status: "ACTIVE_PRODUCTS", count: 5803 }]
                const data = response.data;

                // Find the object with status = "ACTIVE_PRODUCTS"
                const activeItem = data?.find(item => item.status === "ACTIVE_PRODUCTS");

                // Get count or default to 0
                const activeTotalCount = activeItem?.count ?? 0;

                setTotalActiveCount(activeTotalCount);
            } catch (error) {
                console.error("Error fetching bulk order excel data:", error);
            } finally {
                isFetchingRef.current = false;
            }
        };

        fetchData();
    }, [customerIdAsPerRole, hasValidToken]);



    // ✅ FIXED_COLUMNS now exactly matches your Excel column order
    const FIXED_COLUMNS = [
        'Status',
        'Organization',
        'ESP',
        'Domain',
        'Email',
        'First Name',
        'Last Name',
        'Profile Picture (Link)',
        'Preferred Password',
        'Recovery Email',
        'Domain Forwarding',
        'Domain Registrar URL',
        'Domain Registrar Username',
        'Domain Registrar Password',
        'Sending Tool URL',
        'Sending Tool Username',
        'Sending Tool Password',
        'Workspace (Sending tool)',
        'Is Workspace Admin (Yes/No)',
        'Actions',
    ];

    const [excelData, setExcelData] = useState([]);
    const [errors, setErrors] = useState(0)

    // Reset all bulk order data
    const resetBulkOrder = () => {
        setExcelData([]);
        setErrors(0);
    };

    const handleBulkNext2 = async () => {
        setLoading(true);
        try {
            // 🧩 Convert spreadsheet rows into structured objects
            const emailAccounts = excelData.map((row) => {
                const map = FIXED_COLUMNS.reduce((acc, col, idx) => {
                    acc[col] = row[idx]?.value || '';
                    return acc;
                }, {});

                return {
                    organization: map['Organization'] || '',
                    domain: map['Domain'] || '',
                    email: (map['Email'] || '').toLowerCase(),
                    emailProvider: map['ESP'] || '',
                    isWorkspace: map['Is Workspace Admin (Yes/No)'] || '',
                    firstName: map['First Name'] || '',
                    lastName: map['Last Name'] || '',
                    profilePicture: map['Profile Picture (Link)'] || '',
                    preferredPassword: map['Preferred Password'] || '',
                    recoveryEmail: map['Recovery Email'] || '',
                    domainForwarding: map['Domain Forwarding'] || '',
                    domainRegistrarUrl: map['Domain Registrar URL'] || '',
                    domainRegistrarUsername: map['Domain Registrar Username'] || '',
                    domainRegistrarPassword: map['Domain Registrar Password'] || '',
                    sendingToolUrl: map['Sending Tool URL'] || '',
                    sendingToolUsername: map['Sending Tool Username'] || '',
                    sendingToolPassword: map['Sending Tool Password'] || '',
                    sendingToolWorkspace: map['Workspace (Sending tool)'] || '',
                };
            });

            const emailTools = emailAccounts.map((email) => {
                // Convert isWorkspace "yes"/"no" to boolean isWorkspaceAdmin
                const isWorkspaceAdmin = email.isWorkspace?.toLowerCase() === 'yes';

                return {
                    domain: email.domain,
                    email: email.email,
                    emailProvider: email.emailProvider,
                    firstName: email.firstName,
                    fullName: email.fullName,
                    lastName: email.lastName,
                    profilePicture: email.profilePicture,
                    password: email.preferredPassword,
                    isWorkspaceAdmin: isWorkspaceAdmin,
                    recoveryEmail: email.recoveryEmail,
                    forwardingDomain: email.domainForwarding
                };
            });

            // const stepOneData = await getStepData(1, loginCustomerId);

            // Build the order payload
            const orderPayload = {
                userId: userId,
                customerId: userRole === "ADMIN" ? customerId : loginCustomerId,
                orderId: orderId || null,
                orderNumber: orderNumber || null,
                organisationName: "ORGANISATION_IN_SHEET",
                generalInformation: {
                    forwardingDomain: "insheet.com",
                    recoveryEmail: "insheet@inboxlogy.com"
                },
                emailTool: {
                    loginURL: "insheet.com",
                    username: "insheet@demandlogy.com",
                    password: "insheet@Password",
                    workspace: "Yes",
                    email: "insheet@gmail.com",
                    shareAccessConfirmation: true,
                    delegateAccessConfirmation: false
                },
                domainRegistrar: {
                    loginURL: "insheet.com",
                    username: "insheet@demandlogy.com",
                    password: "insheet@Password",
                    workspace: "Yes",
                    email: "insheet@gmail.com",
                    shareAccessConfirmation: true,
                    delegateAccessConfirmation: false
                },
                emailAccounts: emailTools,
            }

            // Generate fresh Excel file from current spreadsheet data
            const generateExcelFile = () => {
                // Convert excelData to array of arrays with values only
                const values = excelData.map((row) =>
                    row
                        .filter((_, i) => FIXED_COLUMNS[i] !== "Actions" && FIXED_COLUMNS[i] !== "Status")
                        .map((c) => c.value || "")
                );

                // Filter headers to exclude Actions and Status columns
                const headers = FIXED_COLUMNS.filter((c) => c !== "Actions" && c !== "Status");

                // Create worksheet
                const ws = XLSX.utils.aoa_to_sheet([headers, ...values]);

                // Create workbook
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Orders");

                // Generate Excel file as binary string
                const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });

                // Convert to Blob
                const buf = new ArrayBuffer(wbout.length);
                const view = new Uint8Array(buf);
                for (let i = 0; i < wbout.length; i++) {
                    view[i] = wbout.charCodeAt(i) & 0xFF;
                }

                return new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            };

            // Create FormData for multipart request
            const formData = new FormData();
            formData.append('order', JSON.stringify(orderPayload));

            // Generate and append the latest Excel file with user modifications
            const excelBlob = generateExcelFile();
            const fileName = `Inboxlogy_Bulk_Order.xlsx`;
            formData.append('file', excelBlob, fileName);
            formData.append("uploadedBy", loginCustomerId)

            const response = await API.post(
                `${import.meta.env.VITE_BACKEND_API}/create-bulk-order`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            const orderDetails = response.data.data;

            setOrderId(orderDetails.orderId);
            setOrderNumber(orderDetails.orderNumber);
            await subscriptionAPI(orderDetails.orderId);


            // Resolve User Payment Gateway
            const resolvePayload = {
                customerId: userRole === "ADMIN" ? customerId : loginCustomerId,
                userId: userId,
                orderNumber: Number(orderDetails.orderNumber),
                orderId: orderDetails.orderId,
                currentMonthAmount: 0,
                recurringAmount: 0,
            };

            try {
                const gatewayResponse = await API.post(
                    `${import.meta.env.VITE_BACKEND_API}/resolve-user-payment-gateway`,
                    resolvePayload
                );

                if (gatewayResponse.status === 200) {
                    setUserPaymentGateway(gatewayResponse.data.paymentGateway);
                }
            } catch (error) {
                console.error("Error resolving payment gateway:", error);
            }

            const requestPayload = {
                orderId: orderDetails.orderId, // Assuming orderId is the first 3 characters
                userId,
                orderNumber: Number(orderDetails.orderNumber),
                customerId: userRole === "ADMIN" ? customerId : loginCustomerId,
                // ...(finalPromoCode && { code: finalPromoCode }) // Add code only if present
            };

            // API request
            let list = await API.post(
                `${import.meta.env.VITE_BACKEND_API}/billinglist?orderType=NEW_ORDER`,
                requestPayload
            );

            const result = list.data[0];
            setData(result);
            if (isBulkOrder) {
                setActiveStep(localStorage.setItem("activeStep", 1) ? parseInt(localStorage.getItem("activeStep")) : 1);
            } else {
                setActiveStep(localStorage.setItem("activeStep", 1) ? parseInt(localStorage.getItem("activeStep")) : 1);
            }
            setLoading(false);
            return true;
        } catch (error) {
            console.error("Error in handleBulkNext2:", error);
            setLoading(false);
            return false;
        }
    }

    return (
        <BulkOrderContext.Provider
            value={{
                excelData,
                setExcelData,
                FIXED_COLUMNS,
                handleBulkNext2,
                setLoading,
                setErrors,
                errors,
                totalActiveCount,
                resetBulkOrder
            }}
        >
            {children}
        </BulkOrderContext.Provider>
    )
}

BulkOrderProvider.propTypes = { children: PropTypes.node.isRequired };
export { BulkOrderContext, BulkOrderProvider };
